import invert from 'lodash/invert';
import lowerFirst from 'lodash/lowerFirst';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import isNil from 'lodash/isNil';
import mapValues from 'lodash/mapValues';
import orderBy from 'lodash/orderBy';
import sumBy from 'lodash/sumBy';
import fromPairs from 'lodash/fromPairs';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import pick from 'lodash/pick';
import sum from 'lodash/sum';
import uniqBy from 'lodash/uniqBy';
import values from 'lodash/values';
import flatMap from 'lodash/flatMap';
import compact from 'lodash/compact';
import {
  BillingCodeViewDTO,
  CustomServiceCodeViewDTO,
  EOBServiceCodePaymentInfoViewDTO,
  EOBDataForClaimViewDTO,
  ClaimAdjustmentViewDTO,
  ClaimDTO,
  HealthCareRemarkCodeDTO,
  EOBClaimPaymentInfoDTO,
  EOBClaimPaymentViewDTO,
  EOBWithInsurancePaymentResponseDTO,
  PatientInfoForEobResponseDTO,
  ClaimSettingsDTO,
  ClaimAdjustmentCarcSettingDTO,
} from 'dtos';
import {
  ServiceAllocation,
  VisitInfo,
} from 'pages/Dashboard/pages/Billing/types';
import {
  formatDate,
  formatDateOnly,
} from 'utils/date';
import {
  isEmptyString,
  isNilOrZero,
  notEmptyString,
  roundNumber,
} from '@populateui/utils';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import type {
  ClaimAdjustment,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBAdjustmentCodePopover';
import {
  EOBForm,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog';
import reject from 'lodash/reject';
import head from 'lodash/head';
import {
  adjustmentCodesByField,
  adjustmentNonMappingFields,
  adjustmentRelatedFields,
  adjustmentTargetMoveRules,
  getAdjustmentBucketStyle,
  readonlyAdjustmentTotalFields,
  AdjustmentRelatedField,
} from 'pages/Dashboard/pages/Billing/Payments/constants';

export const paymentTypes: string[] = [
  'CheckPayment',
  'CashPayment',
  'CardPayment',
  'ACHPayment',
  'NonePayment',
  'ManualCreditTransferPayment',
];

export const eobAmountKeys = Object.freeze([
  'allowedAmount',
  'copay',
  'deductible',
  'coinsurance',
  'insurancePayment',
  'nonCovered',
]);

export const keysForAllocatedByType = Object.freeze({
  payment: [
    'copay',
    'nonCovered',
    'deductible',
    'coinsurance',
  ],
  eob: [
    'copay',
    'deductible',
    'coinsurance',
    'nonCovered',
    'insurancePayment',
  ],
});

export const allocationDefaultKeys = [
  'allowedAmount',
  'copay',
  'deductible',
  'coinsurance',
  'nonCovered',
  'insuranceAdjustments',
  'autoWriteOff',
  'insurancePayment',
  'adjustments',
  'comment',
  'healthCareRemarkCodes',
  'eobId',
];

export function getValidEOBServiceCodePaymentInfos(
  eobServiceCodePaymentInfos: EOBServiceCodePaymentInfoViewDTO[],
): EOBServiceCodePaymentInfoViewDTO[] {
  return reject(eobServiceCodePaymentInfos, (item) => isEmpty(pick(item, allocationDefaultKeys)));
}

export function getClaimIdsWithCPRCMissingServiceAllocation(
  eobServiceCodePaymentInfos: EOBServiceCodePaymentInfoViewDTO[] = [],
  eobClaimPayments: EOBClaimPaymentViewDTO[] = [],
): number[] {
  const claimIdsSeenInServiceAllocations = new Set(
    eobServiceCodePaymentInfos
      .map((item) => item?.claim?.claimId)
      .filter((claimId): claimId is number => (claimId ?? 0) > 0),
  );
  const claimIdsWithValidServiceAllocations = new Set(
    getValidEOBServiceCodePaymentInfos(eobServiceCodePaymentInfos)
      .map((item) => item?.claim?.claimId)
      .filter((claimId): claimId is number => (claimId ?? 0) > 0),
  );

  const claimIdsWithCPRC = new Set(
    eobClaimPayments
      .filter((item) => (
        (item?.remarkCodes?.length ?? 0) > 0
        && (
          (item?.claimPatientId ?? 0) > 0
          || claimIdsSeenInServiceAllocations.has(item?.claimId ?? 0)
        )
      ))
      .map((item) => item?.claimId)
      .filter((claimId): claimId is number => (claimId ?? 0) > 0),
  );

  return Array.from(claimIdsWithCPRC).filter(
    (claimId) => !claimIdsWithValidServiceAllocations.has(claimId),
  );
}

const fieldByAdjustmentCodes = Object.freeze(
  invert(mapValues(adjustmentCodesByField, (info) => `${info?.groupCode}-${info?.reasonCode}`)),
) as Readonly<Record<string, AdjustmentRelatedField>>;

export type AdjustmentExceptionMap = Readonly<Record<string, AdjustmentRelatedField>>;

function getCarcCompositeCode(setting: ClaimAdjustmentCarcSettingDTO): string {
  const { compositeCode, groupCode, reasonCode } = setting;

  if (notEmptyString(compositeCode)) {
    return compositeCode?.trim().toUpperCase() ?? '';
  }

  return notEmptyString(groupCode) && notEmptyString(reasonCode)
    ? `${groupCode?.trim().toUpperCase()}-${reasonCode?.trim().toUpperCase()}`
    : '';
}

function addAdjustmentSettingsToMap(
  result: Record<string, AdjustmentRelatedField>,
  settings: ClaimAdjustmentCarcSettingDTO[] | null | undefined,
  bucket: AdjustmentRelatedField,
) {
  (settings ?? []).forEach((setting) => {
    const compositeCode = getCarcCompositeCode(setting);

    if (isEmptyString(compositeCode)) {
      return;
    }

    if (!isNil(result[compositeCode]) && result[compositeCode] !== bucket) {
      if (import.meta.env.MODE !== 'production') {
        console.warn(
          `CARC ${compositeCode} is configured for multiple adjustment buckets.`,
        );
      }
      return;
    }

    result[compositeCode] = bucket;
  });
}

export function getAdjustmentExceptionMap(
  claimSettings?: Pick<ClaimSettingsDTO,
    'nonCoveredCarcSettings' | 'autoWriteOffCarcSettings'>,
): AdjustmentExceptionMap {
  const result: Record<string, AdjustmentRelatedField> = {};

  addAdjustmentSettingsToMap(
    result,
    claimSettings?.nonCoveredCarcSettings,
    'nonCovered',
  );
  addAdjustmentSettingsToMap(
    result,
    claimSettings?.autoWriteOffCarcSettings,
    'autoWriteOff',
  );

  return Object.freeze(result);
}

function getAdjustmentCompositeCode(
  adjustment: Pick<ClaimAdjustment, 'groupCode' | 'reasonCode'>,
): string {
  return [
    adjustment?.groupCode?.code ?? '',
    adjustment?.reasonCode?.code ?? '',
  ].join('-');
}

export function getAdjustmentBucket(
  adjustment: Pick<ClaimAdjustment, 'groupCode' | 'reasonCode'>,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
): AdjustmentRelatedField {
  const compositeKey = getAdjustmentCompositeCode(adjustment);

  return fieldByAdjustmentCodes[compositeKey]
    ?? adjustmentExceptionMap[compositeKey]
    ?? 'insuranceAdjustments';
}

export function isAdjustmentRelatedField(
  value?: string | null,
): value is AdjustmentRelatedField {
  return adjustmentRelatedFields.includes(value as AdjustmentRelatedField);
}

export function getDefaultAdjustmentBucket(
  adjustment: Pick<ClaimAdjustment, 'groupCode' | 'reasonCode' | 'defaultBucket'>,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
): AdjustmentRelatedField {
  return isAdjustmentRelatedField(adjustment.defaultBucket)
    ? adjustment.defaultBucket
    : getAdjustmentBucket(adjustment, adjustmentExceptionMap);
}

export function getEffectiveAdjustmentTarget(
  adjustment: Pick<ClaimAdjustment, 'groupCode' | 'reasonCode' | 'effectiveBucket' | 'defaultBucket'>,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
): AdjustmentRelatedField {
  if (isAdjustmentRelatedField(adjustment.effectiveBucket)) {
    return adjustment.effectiveBucket;
  }

  return getDefaultAdjustmentBucket(adjustment, adjustmentExceptionMap);
}

export function withAdjustmentTargets(
  adjustments: ClaimAdjustment[] = [],
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
): ClaimAdjustment[] {
  return adjustments.map((adjustment) => {
    const defaultBucket = getDefaultAdjustmentBucket(adjustment, adjustmentExceptionMap);
    const effectiveBucket = isAdjustmentRelatedField(adjustment.effectiveBucket)
      ? adjustment.effectiveBucket
      : defaultBucket;

    return {
      ...adjustment,
      defaultBucket,
      effectiveBucket,
    };
  });
}

export function canMoveAdjustmentToTarget(
  adjustment: Pick<ClaimAdjustment, 'groupCode' | 'reasonCode' | 'effectiveBucket' | 'defaultBucket'
    | 'canMoveToAutoWriteOff' | 'canMoveBackToInsuranceAdjustments'>,
  destinationBucket: AdjustmentRelatedField,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
): boolean {
  const defaultBucket = getDefaultAdjustmentBucket(adjustment, adjustmentExceptionMap);
  const currentBucket = getEffectiveAdjustmentTarget(adjustment, adjustmentExceptionMap);

  if (currentBucket === destinationBucket) {
    return false;
  }

  // use BE flags when available (loaded adjustments)
  if (adjustment.canMoveToAutoWriteOff !== undefined) {
    if (destinationBucket === 'autoWriteOff') {
      return adjustment.canMoveToAutoWriteOff ?? false;
    }
    return destinationBucket === defaultBucket
      && (adjustment.canMoveBackToInsuranceAdjustments ?? false);
  }

  // FE fallback for new CARCs not yet saved
  const allowedBuckets = adjustmentTargetMoveRules[defaultBucket] ?? [];
  if (allowedBuckets.length === 0) return false;

  return (
    allowedBuckets.includes(destinationBucket)
    || (destinationBucket === defaultBucket && allowedBuckets.includes(currentBucket))
  );
}

export function moveAdjustmentTarget(
  adjustments: ClaimAdjustment[],
  adjustmentIndex: number,
  effectiveBucket: AdjustmentRelatedField,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
): ClaimAdjustment[] {
  const normalizedAdjustments = withAdjustmentTargets(adjustments, adjustmentExceptionMap);
  const adjustment = normalizedAdjustments[adjustmentIndex];

  if (!adjustment || !canMoveAdjustmentToTarget(
    adjustment,
    effectiveBucket,
    adjustmentExceptionMap,
  )) {
    return adjustments;
  }

  return normalizedAdjustments.map((item, index) => {
    if (index !== adjustmentIndex) return item;

    const updated = { ...item, effectiveBucket };

    if (updated.canMoveToAutoWriteOff !== undefined) {
      const isMoved = updated.effectiveBucket === 'autoWriteOff'
        && updated.defaultBucket === 'insuranceAdjustments';
      updated.canMoveToAutoWriteOff = !isMoved && updated.defaultBucket === 'insuranceAdjustments';
      updated.canMoveBackToInsuranceAdjustments = isMoved;
    }

    return updated;
  });
}

export function getAdjustmentStyleForCode(
  adjustment: Pick<ClaimAdjustment, 'groupCode' | 'reasonCode' | 'defaultBucket'>,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
) {
  const defaultBucket = getDefaultAdjustmentBucket(adjustment, adjustmentExceptionMap);

  return getAdjustmentBucketStyle(defaultBucket);
}

function bucketToField(value: string | undefined | null): AdjustmentRelatedField | undefined {
  const camel = lowerFirst(value ?? '');
  return isAdjustmentRelatedField(camel) ? camel : undefined;
}

export function normalizeAdjustmentCodes(
  adjustments: ClaimAdjustmentViewDTO[] = [],
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
): ClaimAdjustment[] {
  return withAdjustmentTargets(adjustments.map(({
    reasonCode,
    groupCode,
    amount,
    quantity,
    defaultBucket,
    effectiveBucket,
    isMovedToAutoWriteOff: _isMovedToAutoWriteOff,
    canMoveToAutoWriteOff,
    canMoveBackToInsuranceAdjustments,
    ...rest
  }) => ({
    reasonCode: { code: reasonCode ?? '' },
    groupCode: { code: groupCode ?? '' },
    amount: amount?.toString(),
    defaultBucket: bucketToField(defaultBucket),
    effectiveBucket: bucketToField(effectiveBucket),
    canMoveToAutoWriteOff,
    canMoveBackToInsuranceAdjustments,
    ...rest,
  })), adjustmentExceptionMap);
}

export function serializeAdjustmentCodes(
  adjustments: ClaimAdjustment[] = [],
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
) {
  return withAdjustmentTargets(adjustments, adjustmentExceptionMap).map(({
    reasonCode,
    groupCode,
    amount,
    quantity,
    effectiveBucket,
    defaultBucket,
    canMoveToAutoWriteOff: _c1,
    canMoveBackToInsuranceAdjustments: _c2,
    ...rest
  }) => ({
    reasonCode: reasonCode?.code ?? null,
    groupCode: groupCode?.code ?? null,
    amount: Number(amount) ?? 0,
    quantity: isEmptyString(quantity) ? null : Number(quantity),
    isMovedToAutoWriteOff:
      effectiveBucket === 'autoWriteOff' && defaultBucket === 'insuranceAdjustments' ? true : null,
    ...rest,
  }));
}

export function serializeRemarkCodes(healthCareRemarkCodes: HealthCareRemarkCodeDTO[]) {
  return healthCareRemarkCodes?.map(({
    codeListQualifierCode,
    codeListQualifierCodeValue,
    remarkCode,
    eobServiceCodePaymentInfoId,
    healthCareRemarkCodeId,
  }) => ({
    codeListQualifierCode: codeListQualifierCode ?? 'HE',
    codeListQualifierCodeValue: codeListQualifierCodeValue ?? 'HE',
    remarkCode: remarkCode ?? '',
    eobServiceCodePaymentInfoId: eobServiceCodePaymentInfoId ?? 0,
    healthCareRemarkCodeId: healthCareRemarkCodeId ?? 0,
  })).filter(({ remarkCode }) => notEmptyString(remarkCode));
}
export function calculateEOBAllocated(paymentsInfo: EOBServiceCodePaymentInfoViewDTO[]) {
  return sumBy(paymentsInfo, 'insurancePayment');
}

export function serializeClaimPayment(eobClaimPayments: EOBClaimPaymentViewDTO[] = []) {
  return eobClaimPayments
    .map((item) => ({
      ...item,
      claimId: item?.claimId ?? 0,
      remarkCodes: compact(item?.remarkCodes ?? []).filter(notEmptyString),
    }))
    .filter((item) => (item?.claimId ?? 0) > 0 && (item?.remarkCodes?.length ?? 0) > 0);
}

export function serializeEOB(
  { eob, insurancePayment, payer }: Omit<EOBForm, 'eobDoc'>,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
) {
  const { payerId, payerName } = payer ?? {};
  const eobServiceCodePaymentInfos = getValidEOBServiceCodePaymentInfos(
    eob?.eobServiceCodePaymentInfos ?? [],
  );
  const serializedInsurancePayment = !isNil(insurancePayment)
    ? {
      ...insurancePayment,
      paymentDate: isNil(insurancePayment.paymentDate)
        ? insurancePayment.paymentDate
        : formatDateOnly(insurancePayment.paymentDate),
    }
    : insurancePayment;
  return {
    eob: {
      ...eob,
      insuranceName: payerName ?? '',
      payerIdentificationNumber: isEmptyString(payerId) ? '00000' : payerId,
      eobClaimPayments: serializeClaimPayment(eob?.eobClaimPayments ?? []),
      eobServiceCodePaymentInfos: eobServiceCodePaymentInfos.map((item) => {
        const {
          eobServiceCodePaymentInfoId,
          eobId,
          claim,
          id,
          adjustments,
          allowedAmount,
          deductible,
          copay,
          nonCovered,
          coinsurance,
          insurancePayment,
          visitNoteBillingCodeId,
          insuranceAdjustments,
          autoWriteOff,
          healthCareRemarkCodes,
          comment,
          isExcluded,
        } = item as ServiceAllocation;
        return {
          eobServiceCodePaymentInfoId,
          eobId: eobId ?? eob?.eobId,
          claimId: claim?.claimId,
          visitNoteBillingCodeId: visitNoteBillingCodeId ?? id,
          allowedAmount,
          deductible,
          copay,
          nonCovered,
          coinsurance,
          insurancePayment,
          insuranceAdjustments,
          autoWriteOff,
          adjustments: serializeAdjustmentCodes(adjustments ?? [], adjustmentExceptionMap),
          healthCareRemarkCodes: serializeRemarkCodes(healthCareRemarkCodes ?? []),
          comment: isEmptyString(comment) ? null : comment,
          isExcluded,
        };
      }),
    },
    insurancePayment: serializedInsurancePayment,
  };
}

export type AdjustmentBucketField = 'insuranceAdjustments' | 'autoWriteOff';

export type PatientClaimAllocations = {
  claim: NonNullable<EOBServiceCodePaymentInfoViewDTO['claim']>;
  serviceCodes: ServiceAllocation[];
};

function hasMovableAdjustment(
  item: ServiceAllocation,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
) {
  if ((item.insuranceAdjustments ?? 0) !== 0) {
    return true;
  }

  return (item.adjustments ?? []).some(
    (adjustment) => (
      getDefaultAdjustmentBucket(adjustment, adjustmentExceptionMap) === 'insuranceAdjustments'
    ),
  );
}

export function getPatientClaimAllocations(
  paymentsInfo: EOBServiceCodePaymentInfoViewDTO[],
  patientId: number,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
): PatientClaimAllocations[] {
  const forPatient = paymentsInfo?.filter(
    (item) => item?.claim?.patientId === patientId,
  ) as ServiceAllocation[];
  const groups = groupBy(forPatient, 'claim.claimId');

  return orderBy(
    values(groups)
      .map((serviceCodes) => ({
        claim: head(serviceCodes)?.claim ?? {},
        serviceCodes: serviceCodes.filter(
          (serviceCode) => hasMovableAdjustment(serviceCode, adjustmentExceptionMap),
        ),
      }))
      .filter((group) => group.serviceCodes.length > 0),
    ['claim.claimId'],
    ['asc'],
  );
}

export function getInReviewInfoForEOB(
  paymentsInfo: EOBServiceCodePaymentInfoViewDTO[],
  patients: PatientCompact[],
  eobClaimPayments: EOBClaimPaymentViewDTO[] = [],
): Partial<VisitInfo> {
  const patientsById = keyBy(patients, 'patientId');

  return {
    allocation: orderBy(
      values(groupBy(paymentsInfo, 'claim.patientId')).map((groupedInfo) => {
        const claimIds = new Set(groupedInfo.map((item) => item?.claim?.claimId));
        const patientId = head(groupedInfo)?.claim?.patientId ?? 0;
        return {
          adjustments: compact(flatMap(groupedInfo, 'adjustments')),
          healthCareRemarkCodes: compact(flatMap(groupedInfo, 'healthCareRemarkCodes')),
          eobClaimPayments: eobClaimPayments.filter((p) => claimIds.has(p?.claimId)),
          patientId,
          patientFullName: getDisplayName(patientsById[patientId]?.formattedNames),
          ...fromPairs(
            [...eobAmountKeys, 'billedAmount', 'insuranceAdjustments', 'autoWriteOff'].map((valueKey) => [valueKey, sumBy(groupedInfo, valueKey)]),
          ),
        };
      }),
      'patientFullName',
      'asc',
    ),
  };
}

function getAllocatedTotal(data: EOBServiceCodePaymentInfoViewDTO) {
  return sum(values(pick(data ?? {}, keysForAllocatedByType.payment)));
}

function getSuggestedInsuranceWriteoff(
  data: EOBServiceCodePaymentInfoViewDTO,
  billedAmount: number,
) {
  const total = sum(values(pick(data ?? {}, keysForAllocatedByType.eob)));
  return roundNumber(billedAmount - total);
}

type AllocationMeta = {
  encounterId?: number;
  claim?: ClaimDTO;
  patient?: Partial<PatientCompact>;
};

function getCompactedBreakdown(
  eobBreakdowns: EOBServiceCodePaymentInfoViewDTO[],
): EOBServiceCodePaymentInfoViewDTO {
  const keys = keysForAllocatedByType.payment;
  const result: Record<string, number | null> = {};

  keys.forEach((key) => {
    const values = compact(map(eobBreakdowns, key));
    result[key] = values.length > 0 ? sum(values) : null;
  });

  const comment = compact(map(eobBreakdowns, 'comment')).join(' ');

  return {
    ...result,
    comment,
  };
}

export function hasAllocationData(allocation: ServiceAllocation[]) {
  const keys = [...eobAmountKeys, 'adjustments'];
  return allocation.some((item) => values(pick(item, keys))
    .some((v) => (Array.isArray(v) ? !isEmpty(v) : !isEmptyString(v))));
}

function suppressDuplicateBilledAmounts(
  rows: ServiceAllocation[],
): ServiceAllocation[] {
  const seenCodeIds = new Set<number>();

  return rows.map((row) => {
    const codeId = row.visitNoteBillingCodeId;
    const result = isNil(codeId)
      ? row
      : { ...row, suppressBilledAmount: seenCodeIds.has(codeId) };

    if (!isNil(codeId)) {
      seenCodeIds.add(codeId);
    }
    return result;
  });
}

export function withServiceCodesAllocation(
  codes: Partial<BillingCodeViewDTO & CustomServiceCodeViewDTO>[] = [],
  meta: AllocationMeta = {},
  isOutOfPocket: boolean = false,
  compactEOBPayment: boolean = false,
): ServiceAllocation[] {
  const result = codes.map((item) => {
    const isCPT = !isNil(item.visitNoteBillingCodeId);
    const genericCodeAttributes = isCPT
      ? {
        visitNoteBillingCodeId: item.visitNoteBillingCodeId,
        type: 'cpt',
        id: item.visitNoteBillingCodeId,
        serviceName: item.code ?? '',
        nonCovered: isOutOfPocket ? item.billedAmount : null,
        total: isOutOfPocket ? item.billedAmount : null,
        billedAmount: item?.billedAmount ?? 0,
      } : {
        visitNoteBillingCustomServiceCodeId: item.visitNoteBillingCustomServiceCodeId,
        type: 'custom',
        id: item.visitNoteBillingCustomServiceCodeId,
        serviceName: item.serviceName ?? '',
        nonCovered: item.billedAmount,
        total: item.billedAmount,
      };

    const eobBreakdown = item?.eobServiceCodePaymentInfos ?? [];
    const breakdown = compactEOBPayment && eobBreakdown.length > 1
      ? [getCompactedBreakdown(eobBreakdown)] : eobBreakdown;

    const items = isCPT && !isOutOfPocket && breakdown.length > 0
      ? breakdown.map((info) => ({
        ...info,
        total: getAllocatedTotal(info),
        suggestedInsuranceWriteoff: getSuggestedInsuranceWriteoff(info, item.billedAmount ?? 0),
      })) : [item];

    return items.map((info: EOBServiceCodePaymentInfoViewDTO) => ({
      ...meta,
      patientId: meta?.patient?.patientId ?? 0,
      ...genericCodeAttributes,
      ...info,
      id: !isNilOrZero(info?.eobServiceCodePaymentInfoId)
        ? info.eobServiceCodePaymentInfoId
        : genericCodeAttributes?.id,
    }));
  });

  return suppressDuplicateBilledAmounts(result.flat() as ServiceAllocation[]);
}

function withMissingCanonicalAdjustments(
  info: EOBServiceCodePaymentInfoViewDTO,
): EOBServiceCodePaymentInfoViewDTO {
  const adjustments = info.adjustments ?? [];
  const existingCodes = new Set(
    adjustments.map(({ groupCode, reasonCode }) => `${groupCode ?? ''}-${reasonCode ?? ''}`),
  );

  const synthesizedAdjustments = Object.entries(adjustmentCodesByField).flatMap(
    ([field, codeInfo]) => {
      const amount = info[field as AdjustmentRelatedField];
      const compositeKey = `${codeInfo?.groupCode ?? ''}-${codeInfo?.reasonCode ?? ''}`;

      return isNilOrZero(amount) || existingCodes.has(compositeKey) ? [] : [{
        groupCode: codeInfo?.groupCode ?? '',
        reasonCode: codeInfo?.reasonCode ?? '',
        amount: Number(amount),
      }];
    },
  );

  return synthesizedAdjustments.length > 0
    ? {
      ...info,
      adjustments: [...adjustments, ...synthesizedAdjustments],
    }
    : info;
}

function getAdjustmentExceptionsByField(
  adjustmentExceptionMap: AdjustmentExceptionMap,
): Readonly<Partial<Record<AdjustmentRelatedField, string[]>>> {
  return mapValues(
    groupBy(
      Object.entries(adjustmentExceptionMap),
      ([, relatedField]) => relatedField,
    ),
    (entries) => entries.map(([code]) => code),
  );
}

function enhanceAdjustments(
  adjustments: ClaimAdjustment[],
  id: AdjustmentRelatedField,
  amount: number | null,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
): ClaimAdjustment[] {
  const {
    groupCode: groupCodeByField,
    reasonCode: reasonCodeByField,
  } = adjustmentCodesByField[id] ?? {};

  const adjustmentIndex = adjustments.findIndex(({
    groupCode,
    reasonCode,
  }) => groupCode?.code === groupCodeByField && reasonCode?.code === reasonCodeByField);

  const adjustmentExceptionsByField = getAdjustmentExceptionsByField(adjustmentExceptionMap);
  const relatedAdjustmentsCodes = new Set(adjustmentExceptionsByField[id]);

  const relatedAdjustmentsSum = sum(
    adjustments.filter(
      ({ groupCode, reasonCode }) => relatedAdjustmentsCodes.has(`${groupCode?.code}-${reasonCode?.code}`),
    ).map(({ amount }) => Number(amount ?? 0)),
  );

  const index = adjustmentIndex === -1 ? adjustments.length : adjustmentIndex ?? 0;

  return withAdjustmentTargets(compact([
    ...adjustments.slice(0, index),
    (isNil(amount) ? null : {
      ...(adjustments?.[index] ?? {}),
      groupCode: { code: groupCodeByField },
      reasonCode: { code: reasonCodeByField },
      amount: roundNumber(amount - relatedAdjustmentsSum),
    }),
    ...adjustments.slice(index + 1),
  ]) as ClaimAdjustment[], adjustmentExceptionMap);
}

function computeBaseAmount(
  allocation: ServiceAllocation,
  id: AdjustmentRelatedField,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
) {
  const relatedAdjustmentsSum = sum(
    withAdjustmentTargets(allocation?.adjustments ?? [], adjustmentExceptionMap)
      .filter((adjustment) => getEffectiveAdjustmentTarget(
        adjustment,
        adjustmentExceptionMap,
      ) === id)
      .map(({ amount }) => Number(amount ?? 0)),
  );

  return (allocation[id] ?? 0) - relatedAdjustmentsSum;
}

function resolveReadOnlyAdjustmentTotal(
  baseAmount: number | null | undefined,
  mappedSum: number,
) {
  if (isNil(baseAmount) && mappedSum === 0) {
    return undefined;
  }

  return Number(baseAmount ?? 0) + mappedSum;
}

type ReadonlyAdjustmentTotalField = (typeof readonlyAdjustmentTotalFields)[number];
type NonMappingAdjustmentField = (typeof adjustmentNonMappingFields)[number];

function isReadonlyAdjustmentTotalField(
  field: AdjustmentRelatedField,
): field is ReadonlyAdjustmentTotalField {
  return readonlyAdjustmentTotalFields.includes(field as ReadonlyAdjustmentTotalField);
}

function isNonMappingAdjustmentField(
  field: AdjustmentRelatedField,
): field is NonMappingAdjustmentField {
  return adjustmentNonMappingFields.includes(field as NonMappingAdjustmentField);
}

export function syncWithAdjustments(
  allocation: ServiceAllocation,
  id: string,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
): ServiceAllocation {
  const addition: Partial<ServiceAllocation> = { };
  switch (id) {
    case 'adjustments': {
      const normalizedAdjustments = withAdjustmentTargets(
        allocation.adjustments ?? [],
        adjustmentExceptionMap,
      );
      const mappedSumsByField = readonlyAdjustmentTotalFields.reduce(
        (accumulator, field) => ({
          ...accumulator,
          [field]: 0,
        }),
        {} as Record<(typeof readonlyAdjustmentTotalFields)[number], number>,
      );

      addition.adjustments = normalizedAdjustments;

      normalizedAdjustments.forEach((adjustment) => {
        const field = getEffectiveAdjustmentTarget(adjustment, adjustmentExceptionMap);
        if (!isNonMappingAdjustmentField(field)) {
          addition[field] = Number(addition[field] ?? '0') + Number(adjustment?.amount);
        } else if (isReadonlyAdjustmentTotalField(field)) {
          mappedSumsByField[field] += Number(adjustment?.amount ?? 0);
        } else {
          if (isNil(addition[field])) {
            addition[field] = allocation.adjustmentBaseAmounts?.[field] ?? 0;
          }
          addition[field] = (addition[field] ?? 0) + Number(adjustment?.amount);
        }
      });
      readonlyAdjustmentTotalFields.forEach((field) => {
        addition[field] = resolveReadOnlyAdjustmentTotal(
          allocation.adjustmentBaseAmounts?.[field],
          mappedSumsByField[field],
        );
      });
      break;
    }
    case 'coinsurance':
    case 'deductible':
    case 'copay':
      addition.adjustments = enhanceAdjustments(
        allocation?.adjustments ?? [],
        id,
        isNil(allocation[id]) ? null : Number(allocation[id]),
        adjustmentExceptionMap,
      );
      break;
    // adjustmentNonMappingFields should always match to computeBaseAmount
    case 'autoWriteOff':
    case 'insuranceAdjustments':
    case 'nonCovered':
      addition.adjustmentBaseAmounts = {
        ...(allocation.adjustmentBaseAmounts ?? {}),
        [id]: computeBaseAmount(allocation, id, adjustmentExceptionMap),
      };
      break;
    case 'comment':
      addition.comment = allocation[id];
      break;
    default:
      break;
  }

  return {
    ...allocation,
    ...addition,
  };
}

function getUnallocatedAmount(allocation: ServiceAllocation[]) {
  const keys = [...keysForAllocatedByType.eob, 'insuranceAdjustments', 'autoWriteOff'];
  const groupedByCode = groupBy(
    allocation,
    (row) => row.visitNoteBillingCodeId ?? row.visitNoteBillingCustomServiceCodeId ?? row.id,
  );
  return sum(values(groupedByCode).map((rows) => {
    const { billedAmount = 0 } = head(rows) ?? {};
    const totalAllocated = sum(rows.flatMap((item) => values(pick(item, keys))));
    return (billedAmount ?? 0) - totalAllocated;
  }));
}

export function populateEOBAllocation(
  eobNumber: string,
  patient: PatientCompact,
  data: EOBDataForClaimViewDTO[],
  allocation: EOBServiceCodePaymentInfoViewDTO[] = [],
  claimPayments: EOBClaimPaymentInfoDTO[] = [],
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
): VisitInfo[] {
  const serviceCodePayments = uniqBy(map(data
    .map(({ billingCodes }) => billingCodes).flat(), 'eobServiceCodePaymentInfos')
    .flat()
    .map(({ adjustments, ...item }) => ({
      ...item,
      adjustments: normalizeAdjustmentCodes(adjustments, adjustmentExceptionMap),
    })), 'eobServiceCodePaymentInfoId');

  const groupedByClaimId = groupBy(allocation, 'claim.claimId');
  const claimPaymentsByClaimId = groupBy(claimPayments, 'claimId');
  const billedAmountByCodeId = Object.fromEntries(
    data.flatMap(({ billingCodes }) => (
      (billingCodes ?? []).map((code) => [code.visitNoteBillingCodeId, code.billedAmount ?? 0])
    )),
  );

  return data.map((item) => {
    const claimId = item?.claim?.claimId ?? 0;
    const allocation = withServiceCodesAllocation((item?.billingCodes ?? []).map((code) => ({
      ...code,
      eobServiceCodePaymentInfos: values(groupedByClaimId[claimId])
        .filter((item) => item?.visitNoteBillingCodeId === code?.visitNoteBillingCodeId),
    })), {
      encounterId: item?.encounterId ?? 0,
      claim: item?.claim ?? {},
      patient,
    });

    const eobNumbers = new Set(item.eobNumbers ?? []);
    const relatedAllocation = suppressDuplicateBilledAmounts(
      orderBy(serviceCodePayments.filter((payment) => (
        eobNumbers.has(payment.eobNumber)
        && payment.eobNumber !== eobNumber
        && payment?.claimId === claimId
      )).map(({ visitNoteBillingCode, ...payment }) => ({
        ...payment,
        serviceName: visitNoteBillingCode?.code ?? '--',
        billedAmount: billedAmountByCodeId[payment.visitNoteBillingCodeId ?? 0],
      })), ['eobId']) as ServiceAllocation[],
    );

    const eobClaimPayments = claimPaymentsByClaimId[claimId];

    const insurancePaymentTotal = sum(compact(map(allocation ?? [], 'insurancePayment'))) ?? 0;

    const unallocated = getUnallocatedAmount(allocation);

    return ({
      ...item,
      encounterId: item?.encounterId ?? 0,
      eobNumbers,
      hasEOBs: (item?.eobNumbers ?? []).length > 0,
      patient,
      patientId: patient?.patientId ?? 0,
      visitDate: item.visitDateTime ?? '',
      formattedVisitDate: formatDate(item?.visitDateTime ?? ''),
      allocation,
      relatedAllocation,
      eobClaimPayments,
      unallocated,
      insurancePaymentTotal,
    });
  });
}

export function formatEOBData(
  eobData: EOBWithInsurancePaymentResponseDTO,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
) {
  const { eob, insurancePayment } = eobData;
  const {
    eobServiceCodePaymentInfos,
    insuranceName,
    payerIdentificationNumber,
    ..._eob
  } = eob ?? {};

  return {
    eob: {
      ..._eob,
      eobServiceCodePaymentInfos: (eobServiceCodePaymentInfos ?? [])
        .map(({ adjustments, ...info }: EOBServiceCodePaymentInfoViewDTO) => ({
          ...info,
          adjustments: normalizeAdjustmentCodes(adjustments ?? [], adjustmentExceptionMap),
          id: !isNilOrZero(info?.eobServiceCodePaymentInfoId)
            ? info.eobServiceCodePaymentInfoId
            : info?.visitNoteBillingCodeId,
        })),
    },
    insurancePayment,
    payer: {
      payerName: insuranceName,
      payerId: payerIdentificationNumber,
    },
  };
}

export function formatParsedEOBData(
  eobData: EOBWithInsurancePaymentResponseDTO,
  adjustmentExceptionMap: AdjustmentExceptionMap = {},
) {
  const { eob, insurancePayment } = eobData;
  const {
    eobServiceCodePaymentInfos,
    insuranceName,
    payerIdentificationNumber,
    ..._eob
  } = eob ?? {};

  return {
    eob: {
      ..._eob,
      eobServiceCodePaymentInfos: (eobServiceCodePaymentInfos ?? [])
        .map((
          rawInfo: EOBServiceCodePaymentInfoViewDTO,
        ) => withMissingCanonicalAdjustments(rawInfo))
        .map(({ adjustments, ...info }: EOBServiceCodePaymentInfoViewDTO) => ({
          ...info,
          adjustments: normalizeAdjustmentCodes(adjustments ?? [], adjustmentExceptionMap),
          id: !isNilOrZero(info?.eobServiceCodePaymentInfoId)
            ? info.eobServiceCodePaymentInfoId
            : info?.visitNoteBillingCodeId,
        })),
    },
    insurancePayment,
    payer: {
      payerName: insuranceName,
      payerId: payerIdentificationNumber,
    },
  };
}

export function formatPatientInfoList(patientInfoList: PatientInfoForEobResponseDTO[]) {
  return orderBy(
      (patientInfoList ?? []).map(({
        formattedNames,
        patientDOB,
        patientId,
      }) => ({
        patientId,
        fullName: formattedNames?.fullName,
        indexByLastNameWithCredentials: formattedNames?.indexByLastNameWithCredentials,
        indexByLastName: formattedNames?.indexByLastName,
        dateOfBirth: patientDOB,
        formattedDateOfBirth: formatDate(patientDOB ?? ''),
        formattedNames,
      })) as PatientCompact[],
      'indexByLastNameWithCredentials',
      'asc',
  );
}
